import * as Sentry from '@sentry/react';

import { isDev } from '~/dev/utils/isDev';

/** Stable tag values — never localized labels, which would break filtering. */
export type FeedbackSentiment = 'positive' | 'neutral' | 'negative';
export type FeedbackTopic = 'bug' | 'idea' | 'question' | 'other';

interface SubmitCLMEvolutionFeedbackParams {
  message: string;
  /** Route template of the page the feedback was sent from. */
  page: string;
  email?: string;
  name?: string;
  sentiment?: FeedbackSentiment;
  topics?: FeedbackTopic[];
}

export const submitCLMEvolutionFeedback = ({
  message,
  page,
  email,
  name,
  sentiment,
  topics,
}: SubmitCLMEvolutionFeedbackParams) => {
  if (isDev) {
    console.info('[clm-evolution feedback]', {
      message,
      page,
      sentiment,
      topics,
    });
    return;
  }

  Sentry.captureFeedback(
    {
      message,
      name,
      email,
      url: window.location.href,
      source: 'clm-evolution',
    },
    {
      includeReplay: true,
      captureContext: {
        tags: {
          feedback_type: 'clm-evolution',
          feedback_page: page,
          // Left off entirely when unanswered, so Sentry groups them under
          // "(none)" rather than inventing a value we would have to explain.
          ...(sentiment && { feedback_sentiment: sentiment }),
          // A Sentry tag holds one value, so a multi-pick answer joins into a
          // single comma-separated key; one tag per topic keeps each of them
          // filterable on its own.
          ...(topics?.length && {
            feedback_topic: topics.join(','),
            ...Object.fromEntries(
              topics.map((topic) => [`feedback_topic_${topic}`, 'true']),
            ),
          }),
        },
      },
    },
  );
};
